import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  GridColumn,
  GridNoRecords,
  GridToolbar,
  getSelectedState,
} from "@progress/kendo-react-grid";
import { ComboBox, DropDownList } from "@progress/kendo-react-dropdowns";
import { Label } from "@progress/kendo-react-labels";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { Checkbox } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { ProgressBar } from "@progress/kendo-react-progressbars";
import {
  chevronDownIcon,
  arrowRotateCwIcon as refreshIcon,
} from "@progress/kendo-svg-icons";
import { FaEllipsisV, FaTrashAlt } from "react-icons/fa";
import { MdVerified } from "react-icons/md";
import { IoIosWarning } from "react-icons/io";
import { TbArrowsExchange } from "react-icons/tb";

import { buildMappedGridData } from "components/Activities/activityUtils";

import { FilterComponent } from "components/common/FiltersComponent";
import { HandleGridBody } from "components/common/HandleGridBody";
import { GRID_PAGE_SIZES, RedirectCell } from "components/common/GridComponents";
import SearchTextBox from "components/SearchComponent";
import CalenderCustomToggleButton from "components/common/CalenderCustomToogleButton";
import { formatDateForAPI, DateFormat } from "components/common/formatter";
import { getAccessRightsData, useCurrentUser } from "services/currentUserService";
import { fetchTasksSummary } from "services/tasksService";
import {
  useCurrentUserSettings,
  updateCurrentUserSettings,
} from "services/userSettingsService";
import CaseTaskForm from "pages/CaseFile/CaseTaskForm";
import TasksAllocationModal from "./TasksAllocationModal";

import styles from "./Tasks.module.scss";

const USER_SETTING_PAGE_SIZE_KEY = "TasksPageSize";
const TASK_DATA_ITEM_KEY = "taskId";
const TASK_SELECTED_FIELD = "selected";
const SHOW_DELETE_BULK_ACTION = false;

const isValidDate = (d) =>
  d instanceof Date && !Number.isNaN(d.getTime());

const STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Due", value: 1 },
  { label: "Overdue", value: 2 },
  { label: "Completed", value: 5 },
];

const getDisplayStatus = (item) => {
  if (item?.completed) return "Completed";
  if (item?.isOverdue) return "Overdue";
  if (item?.isDueToday) return "Due";
  return "Pending";
};

const Tasks = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const { data: userSettings } = useCurrentUserSettings();
  const summary = useSelector((s) => s.tasksList?.summary) || {
    totalTasks: 0,
    totalDue: 0,
    totalOverdue: 0,
    totalCompleted: 0,
  };

  const searchRef = useRef(null);

  const [allowAccessData, setAllowAccessData] = useState({});
  const [filterState, setFilterState] = useState({
    branchId: { branchName: "ALL", branchId: -2 },
    teamId: { teamName: "ALL", teamID: -2 },
    createdBy: { display: "ALL", userRef: "ALL" },
    feeEarnerRef: { display: "ALL", userRef: "ALL" },
    status: STATUS_OPTIONS[0],
  });

  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [toDate, setToDate] = useState(() => new Date());
  const [fromChecked, setFromChecked] = useState(false);
  const [toChecked, setToChecked] = useState(false);

  const [searchValue, setSearchValue] = useState("");
  const [updateFilter, setUpdateFilter] = useState(false);
  const [isRefreshed, setIsRefreshed] = useState(false);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [feeEarnerInitialized, setFeeEarnerInitialized] = useState(false);

  const [list, setList] = useState({ data: [], total: 0 });

  const [viewTask, setViewTask] = useState(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [selectedState, setSelectedState] = useState({});
  const [showAllocateModal, setShowAllocateModal] = useState(false);

  const idGetter = useCallback((item) => item[TASK_DATA_ITEM_KEY], []);

  const [dataState, setDataState] = useState({
    take:
      Number(
        userSettings?.find((s) => s.key === USER_SETTING_PAGE_SIZE_KEY)?.value,
      ) || 200,
    skip: 0,
    sort: [{ field: "dateDueBy", dir: "desc" }],
  });

  const pageable = useMemo(() => ({ pageSizes: GRID_PAGE_SIZES }), []);

  useEffect(() => {
    const loadAccess = async () => {
      try {
        const data = await getAccessRightsData();
        setAllowAccessData(data?.data?.data || {});
      } catch (error) {}
    };
    loadAccess();
  }, []);

  // Sync persisted page size into dataState when settings load
  useEffect(() => {
    const saved = Number(
      userSettings?.find((s) => s.key === USER_SETTING_PAGE_SIZE_KEY)?.value,
    );
    if (saved && saved !== dataState.take) {
      setDataState((prev) => ({ ...prev, take: saved, skip: 0 }));
    }
  }, [userSettings]);

  // Default Allocated To to the current user (once)
  useEffect(() => {
    if (feeEarnerInitialized) return;
    if (!currentUser?.userRef) return;
    setFilterState((prev) => ({
      ...prev,
      feeEarnerRef: {
        display:
          currentUser.display ||
          currentUser.displayname ||
          currentUser.userRef,
        userRef: currentUser.userRef,
      },
    }));
    setFeeEarnerInitialized(true);
  }, [currentUser, feeEarnerInitialized]);

  // Fallback: if access data has no branches, FilterComponent won't render and
  // never calls handleFilterUpdate, so mark filters initialized ourselves.
  useEffect(() => {
    if (filtersInitialized) return;
    if (!allowAccessData) return;
    if (allowAccessData.branches?.length > 0) return;
    setFiltersInitialized(true);
  }, [allowAccessData, filtersInitialized]);

  const fD =
    fromChecked && isValidDate(fromDate) ? formatDateForAPI(fromDate) : null;
  const tD = toChecked && isValidDate(toDate) ? formatDateForAPI(toDate) : null;

  useEffect(() => {
    if (!filtersInitialized) return;
    if (!feeEarnerInitialized) return;
    dispatch(
      fetchTasksSummary({
        branchId: filterState.branchId?.branchId,
        teamId: filterState.teamId?.teamID,
        createdBy: filterState.createdBy?.userRef,
        feeEarnerRef: filterState.feeEarnerRef?.userRef,
        fD,
        tD,
        status: filterState.status?.value,
        searchValue,
      }),
    );
  }, [
    dispatch,
    filtersInitialized,
    feeEarnerInitialized,
    filterState,
    fD,
    tD,
    searchValue,
    isRefreshed,
  ]);

  useEffect(() => {
    if (updateFilter) setUpdateFilter(false);
  }, [updateFilter]);

  const handleFilterUpdate = (filters) => {
    clearSelection();
    const branch = allowAccessData?.branches?.find(
      (b) => b.branchId === filters.branch,
    );
    const team = branch?.teams?.find((t) => t.teamID === filters.teamId);

    setFiltersInitialized(true);
    setFilterState((prev) => ({
      ...prev,
      branchId: {
        branchName: branch?.branchName || "ALL",
        branchId: filters.branch,
      },
      teamId: {
        teamName: team?.teamName || "ALL",
        teamID: filters.teamId,
      },
    }));
    setUpdateFilter(true);
  };

  const setFilterStateAndFlag = (key, value) => {
    clearSelection();
    setFilterState((prev) => ({ ...prev, [key]: value }));
    setUpdateFilter(true);
  };

  const feeEarnerOptions = useMemo(() => {
    const branches = allowAccessData?.branches || [];
    const selectedBranchId = filterState.branchId?.branchId;
    const selectedTeamId = filterState.teamId?.teamID;

    const teamPool = branches
      .filter((b) => selectedBranchId === -2 || b.branchId === selectedBranchId)
      .flatMap((b) => b.teams || []);

    const earnerPool = teamPool
      .filter((t) => selectedTeamId === -2 || t.teamID === selectedTeamId)
      .flatMap((t) => t.feeEarners || []);

    // Deduplicate by userRef
    const seen = new Set();
    const earners = earnerPool
      .filter((fe) => {
        if (!fe?.userRef || fe.userRef === "ALL") return false;
        if (seen.has(fe.userRef)) return false;
        seen.add(fe.userRef);
        return true;
      })
      .map((fe) => ({
        ...fe,
        display: fe.displayname
          ? `${fe.userRef} - ${fe.displayname}`
          : fe.userRef,
      }));

    return [{ display: "ALL", userRef: "ALL" }, ...earners];
  }, [allowAccessData, filterState.branchId, filterState.teamId]);

  const dataStateChange = async (e) => {
    const newPageSize = e.dataState.take;
    const currentPageSize = dataState.take;

    const next = {
      ...e.dataState,
      skip: newPageSize !== currentPageSize ? 0 : e.dataState.skip,
    };

    if (newPageSize !== currentPageSize) {
      try {
        await updateCurrentUserSettings([
          { key: USER_SETTING_PAGE_SIZE_KEY, value: newPageSize },
        ]);
      } catch (err) {
        console.error("Failed to update page size", err);
      }
    }

    if (next.skip !== dataState.skip) {
      setSelectedState({});
    }

    setDataState(next);
    setUpdateFilter(true);
  };

  const dataReceived = (data) => {
    if (data) setList(data);
  };

  const clearSelection = useCallback(() => setSelectedState({}), []);

  const mappedData = useMemo(
    () => buildMappedGridData(list, selectedState, idGetter, TASK_SELECTED_FIELD),
    [list, selectedState, idGetter],
  );

  const selectedTasks = useMemo(
    () =>
      (list?.data || []).filter((item) => selectedState[idGetter(item)]),
    [list?.data, selectedState, idGetter],
  );

  const selectedCount = selectedTasks.length;

  const onSelectionChange = useCallback(
    (event) => {
      const newSelectedState = getSelectedState({
        event,
        selectedState,
        dataItemKey: TASK_DATA_ITEM_KEY,
      });
      setSelectedState(newSelectedState);
    },
    [selectedState],
  );

  const headerCheckboxValue = useMemo(() => {
    const rows = mappedData?.data || [];
    if (!rows.length) return false;
    const selectedOnPage = rows.filter(
      (item) => selectedState[idGetter(item)],
    ).length;
    if (selectedOnPage === 0) return false;
    if (selectedOnPage === rows.length) return true;
    return null;
  }, [mappedData?.data, selectedState, idGetter]);

  const handleSelectAllHeader = useCallback(
    (event) => {
      const checked = event.value;
      const newSelectedState = { ...selectedState };
      (mappedData?.data || []).forEach((item) => {
        const key = idGetter(item);
        if (checked) {
          newSelectedState[key] = true;
        } else {
          delete newSelectedState[key];
        }
      });
      setSelectedState(newSelectedState);
    },
    [mappedData?.data, selectedState, idGetter],
  );

  const SelectAllHeaderCell = () => (
    <th className="text-center">
      <div className="d-flex align-items-center justify-content-center pt-1">
        <Checkbox
          checked={headerCheckboxValue}
          onChange={handleSelectAllHeader}
        />
      </div>
    </th>
  );

  const handleRefresh = () => {
    clearSelection();
    setDataState((prev) => ({ ...prev, skip: 0 }));
    setIsRefreshed((v) => !v);
    setUpdateFilter(true);
  };

  const handleAllocateSuccess = () => {
    clearSelection();
    setIsRefreshed((v) => !v);
    setUpdateFilter(true);
  };

  const handleSearchChange = (value) => {
    clearSelection();
    setSearchValue(value || "");
    setUpdateFilter(true);
  };

  const DateCell = (props) => {
    const dateField = props.dataItem[props.field];
    return (
      <td className={props.className}>
        {dateField ? DateFormat(dateField) : ""}
      </td>
    );
  };

  const StatusCell = (props) => {
    const label = getDisplayStatus(props.dataItem);
    const tone = label.toLowerCase();
    return (
      <td className={props.className}>
        <span className={`${styles.statusPill} ${styles[`status-${tone}`]}`}>
          {label}
        </span>
      </td>
    );
  };

  const ViewCell = (props) => {
    if (!props.dataItem?.taskId) return <td className={props.className} />;
    return (
      <RedirectCell
        title="View task"
        handleClick={() => {
          setViewTask(props.dataItem);
          setShowTaskForm(true);
        }}
      />
    );
  };

  const MatterRefCell = (props) => (
    <td className={props.className}>
      <span
        style={{ cursor: "pointer" }}
        onClick={() => {
          try {
            window.chrome.webview.postMessage(
              "CasefileOpened:" + props.dataItem.matterId,
            );
          } catch (error) {}
          navigate(`/matters/${props.dataItem.matterId}`);
        }}
      >
        <u>{props.dataItem.matterRef || ""}</u>
      </span>
    </td>
  );

  const summaryTotal = Math.max(summary?.totalTasks || 0, 1);

  return (
    <div className="d-flex flex-column h-100 px-4">
      <div
        className={`d-flex align-items-end gap-2 py-3 justify-content-between ${styles.actionBarRow}`}
      >
        <div className="d-flex gap-2 align-items-end flex-wrap">
          {allowAccessData?.branches && (
            <FilterComponent
              allowAccessData={allowAccessData}
              inlineLayout={true}
              disabledFeeEarner={true}
              filterWidth="150px"
              updateFilters={handleFilterUpdate}
            />
          )}

          {allowAccessData?.accessAllFeeEarnersInTeam && (
            <PersonFilter
              label="Created By"
              placeholder="Search Created By"
              options={feeEarnerOptions}
              value={filterState.createdBy}
              onChange={(v) =>
                setFilterStateAndFlag("createdBy", v || {
                  display: "ALL",
                  userRef: "ALL",
                })
              }
            />
          )}

          {allowAccessData?.accessAllFeeEarnersInTeam && (
            <PersonFilter
              label="Allocated To"
              placeholder="Search Allocated To"
              options={feeEarnerOptions}
              value={filterState.feeEarnerRef}
              onChange={(v) =>
                setFilterStateAndFlag("feeEarnerRef", v || {
                  display: "ALL",
                  userRef: "ALL",
                })
              }
            />
          )}

          <div className={styles.filterItem}>
            <div>
              <Label editorId="from" className="me-5 pe-5">
                From
              </Label>
              <Label editorId="to" className="ms-4 ps-1">
                To
              </Label>
            </div>
            <div className="date-form-to-wrapper">
              <Checkbox
                className="ms-2 mb-auto"
                value={fromChecked}
                onChange={(e) => {
                  setFromChecked(e.value);
                  setUpdateFilter(true);
                }}
              />
              <div className="from">
                <DatePicker
                  id="from"
                  className="date-picker pe-2"
                  format="dd/MM/yyyy"
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.value);
                    if (isValidDate(e.value)) {
                      setFromChecked(true);
                      setUpdateFilter(true);
                    }
                  }}
                  toggleButton={CalenderCustomToggleButton}
                />
              </div>
              <div className="divider m-1"></div>
              <Checkbox
                className="ms-2 mt-auto mb-auto"
                value={toChecked}
                onChange={(e) => {
                  setToChecked(e.value);
                  setUpdateFilter(true);
                }}
              />
              <div className="to">
                <DatePicker
                  id="to"
                  className="date-picker pe-2"
                  format="dd/MM/yyyy"
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.value);
                    if (isValidDate(e.value)) {
                      setToChecked(true);
                      setUpdateFilter(true);
                    }
                  }}
                  toggleButton={CalenderCustomToggleButton}
                />
              </div>
            </div>
          </div>

          <div className={styles.filterItem} style={{ width: "150px" }}>
            <Label className="filter-font mb-1">Status</Label>
            <DropDownList
              style={{ width: "100%", height: "40px" }}
              data={STATUS_OPTIONS}
              textField="label"
              dataItemKey="value"
              svgIcon={chevronDownIcon}
              value={filterState.status}
              onChange={(e) =>
                setFilterStateAndFlag("status", e.value || STATUS_OPTIONS[0])
              }
            />
          </div>
        </div>

        <div className="d-flex gap-1 align-items-end">
          <div style={{ minWidth: "180px", maxWidth: "260px" }}>
            <SearchTextBox
              width="100%"
              height="40px"
              ref={searchRef}
              changeFilterValue={handleSearchChange}
            />
          </div>
          <Button
            className={`common-btn ${styles.customBtn}`}
            svgIcon={refreshIcon}
            onClick={handleRefresh}
            title="Refresh"
          />
        </div>
      </div>

      <div className={styles.summaryCards}>
        <SummaryCard
          tone="completed"
          icon={<MdVerified />}
          label="Completed"
          value={summary?.totalCompleted || 0}
          total={summary?.totalTasks || 0}
          progressMax={summaryTotal}
        />
        <SummaryCard
          tone="due"
          icon={<IoIosWarning />}
          label="Due"
          value={summary?.totalDue || 0}
          total={summary?.totalTasks || 0}
          progressMax={summaryTotal}
        />
        <SummaryCard
          tone="overdue"
          icon={<IoIosWarning />}
          label="Overdue"
          value={summary?.totalOverdue || 0}
          total={summary?.totalTasks || 0}
          progressMax={summaryTotal}
        />
      </div>

      <div className="flex-grow-1" style={{ minHeight: 0, maxHeight: "calc(100vh - 340px)" }}>
        <Grid
          {...dataState}
          data={mappedData}
          sortable={true}
          dataItemKey={TASK_DATA_ITEM_KEY}
          selectedField={TASK_SELECTED_FIELD}
          selectable={{
            enabled: true,
            drag: false,
            cell: false,
            mode: "multiple",
          }}
          onSelectionChange={onSelectionChange}
          onDataStateChange={dataStateChange}
          pageable={pageable}
          pageSize={dataState.take}
          className="h-100 overflow-auto fs-md grid-row-select"
        >
          <GridToolbar className={styles.selectionToolbar}>
            <div
              className={`d-flex align-items-center w-100 gap-2 ${
                selectedCount > 0 ? "justify-content-between" : "justify-content-end"
              }`}
            >
              {selectedCount > 0 && (
                <div className="d-flex align-items-center gap-2">
                  <span className={styles.selectedCountBadge}>
                    {selectedCount}
                  </span>
                  <span className={styles.selectedCountLabel}>Selected</span>
                </div>
              )}
              <div className="d-flex align-items-center gap-2">
                <Button
                  className={styles.toolbarBtn}
                  onClick={() => setShowAllocateModal(true)}
                  disabled={selectedCount === 0}
                  title="Allocate to"
                >
                  <TbArrowsExchange size={18} className={styles.toolbarBtnIcon} />
                  Allocate to
                </Button>
                {SHOW_DELETE_BULK_ACTION && (
                  <Button
                    className={styles.toolbarBtn}
                    disabled={selectedCount === 0}
                    title="Delete"
                  >
                    <FaTrashAlt size={16} />
                    Delete
                  </Button>
                )}
              </div>
            </div>
          </GridToolbar>
          <GridNoRecords>
            {list?.data?.length === 0 ? "No record available" : ""}
          </GridNoRecords>
          <GridColumn
            field={TASK_SELECTED_FIELD}
            width="50px"
            sortable={false}
            headerCell={SelectAllHeaderCell}
          />
          <GridColumn field="date" title="Date" cell={DateCell} width="110px" />
          <GridColumn
            field="matterRef"
            title="Matter Ref"
            cell={MatterRefCell}
            width="130px"
          />
          <GridColumn field="details" title="Details" />
          <GridColumn
            field="addedByUserRef"
            title="Created by"
            width="120px"
          />
          <GridColumn
            field="feeEarnerRef"
            title="Allocated to"
            width="120px"
          />
          <GridColumn
            field="dateDueBy"
            title="Due Date"
            cell={DateCell}
            width="120px"
          />
          <GridColumn
            field="status"
            title="Status"
            cell={StatusCell}
            width="120px"
          />
          <GridColumn
            title=""
            cell={ViewCell}
            width="40px"
            sortable={false}
          />
        </Grid>
      </div>

      {showAllocateModal && (
        <TasksAllocationModal
          tasks={selectedTasks}
          onClose={() => setShowAllocateModal(false)}
          onSuccess={handleAllocateSuccess}
        />
      )}

      {showTaskForm && viewTask && (
        <CaseTaskForm
          matterId={viewTask.matterId}
          addFormText={viewTask.matterRef || ""}
          feeEarnerRef={viewTask.feeEarnerRef}
          isView={true}
          viewTask={viewTask}
          isRefreshed={isRefreshed}
          setIsRefreshed={setIsRefreshed}
          setCloseModal={() => {
            setShowTaskForm(false);
            setViewTask(null);
          }}
        />
      )}

      {filtersInitialized && feeEarnerInitialized && (
        <HandleGridBody
          currentComponent="tasks"
          dataState={dataState}
          branchId={filterState.branchId?.branchId}
          teamId={filterState.teamId?.teamID}
          createdBy={filterState.createdBy?.userRef}
          feeEarnerRef={filterState.feeEarnerRef?.userRef}
          fD={fD}
          tD={tD}
          status={filterState.status?.value}
          searchValue={searchValue}
          updateFilter={updateFilter}
          refresh={isRefreshed}
          onDataReceived={dataReceived}
        />
      )}
    </div>
  );
};

const PersonFilter = ({ label, placeholder, options, value, onChange }) => (
  <div className={styles.filterItem} style={{ width: "150px" }}>
    <Label className="filter-font mb-1">{label}</Label>
    <ComboBox
      style={{ width: "100%", height: "40px" }}
      data={options}
      textField="display"
      dataItemKey="userRef"
      filterable={true}
      svgIcon={chevronDownIcon}
      value={value}
      onChange={(e) => onChange(e.value)}
      placeholder={placeholder}
    />
  </div>
);

const SummaryCard = ({ tone, icon, label, value, total, progressMax }) => {
  return (
    <div className={styles.summaryCard}>
      <div className={`${styles.iconWrap} ${styles[`icon-${tone}`]}`}>
        {icon}
      </div>
      <div className={styles.cardBody}>
        <div className={styles.cardHeaderTop}>
          <span className={styles.cardLabel}>{label}</span>
        </div>
        <div className={styles.progressRow}>
          <div className={styles[`progress-${tone}`]} style={{ flex: 1 }}>
            <ProgressBar
              min={0}
              max={progressMax}
              value={value}
              labelVisible={false}
            />
          </div>
          <span className={styles.cardCount}>
            <span className={styles.cardCountValue}>
              {String(value).padStart(2, "0")}
            </span>
            <span className={styles.cardCountTotal}>
              /{String(total).padStart(2, "0")}
            </span>
          </span>
        </div>
      </div>
      <FaEllipsisV className={styles.menuDots} />
    </div>
  );
};

export default Tasks;
